import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ApiResponse } from '@app/types';

export interface UploadedFile {
  original_name: string;
  url: string;
  path: string;
  contentType: string;
  size: number;
  contentHash: string;
}

export interface UploadResponse {
  successful: UploadedFile[];
  failed: { original_name: string; error: string }[];
}

@Injectable({
  providedIn: 'root',
})
export class FileService {
  private readonly http = inject(HttpClient);
  private readonly API_URL = environment.API_URL;

  uploadFiles(files: File[], pathPrefix: string, documentType: string): Observable<UploadResponse> {
    if (!files || files.length === 0) {
      return of({ successful: [], failed: [] });
    }

    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file, file.name);
    });
    formData.append('path_prefix', pathPrefix);
    formData.append('document_type', documentType); // Añadir el tipo de documento

    // La API devuelve un objeto que contiene directamente 'successful' y 'failed'
    return this.http.post<UploadResponse>(`${this.API_URL}/files/upload`, formData).pipe(
      map(response => {
        // CORRECCIÓN: Mapear directamente la respuesta que sí tiene 'successful' y 'failed'
        return {
          successful: response.successful || [],
          failed: response.failed || []
        };
      }),
      catchError(error => {
        console.error('Error en la subida de archivos:', error);
        const failedFiles = files.map(f => ({ original_name: f.name, error: 'Error de red o del servidor' }));
        return of({ successful: [], failed: failedFiles });
      })
    );
  }

  /**
   * Obtiene una URL segura y temporal para ver un archivo a través del proxy del backend.
   * @param s3Key La ruta del archivo en S3
   * @returns Observable con la URL temporal segura
   */
  getSecureFileUrl(s3Key: string): Observable<string> {
    return this.http.get<{ url: string }>(
      `${this.API_URL}/files/view/${s3Key}`
    ).pipe(
      map(response => response.url),
      catchError(error => {
        console.error('Error al obtener URL segura para el archivo:', error);
        // En caso de error, devolver una URL vacía o manejar el error apropiadamente
        return of('');
      })
    );
  }
}
